import { Link } from 'react-router-dom'
import { BlogLink } from '@/components/blog/BlogLink'
import type { FaqItem } from '@/lib/jsonLd'

interface PoradnikCardProps {
  slug: string
  title: string
  description: string
}

/**
 * Whole-card-clickable article preview. The entire <Link> is the click target;
 * the title gets the underline-on-hover via `group-hover:` so it still reads
 * as the "primary action" within the card. Inline BlogLink (used elsewhere
 * inside paragraphs) keeps its original behavior — this is for full-card
 * variants only.
 */
function PoradnikCard({ slug, title, description }: PoradnikCardProps) {
  return (
    <li>
      <Link
        to={`/blog/${slug}`}
        className="group block h-full rounded-xl border border-border/60 p-4 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
      >
        <span className="block text-primary font-semibold group-hover:underline underline-offset-2 decoration-primary/40">
          {title}
        </span>
        <p className="text-xs text-muted-foreground mt-1.5">
          {description}
        </p>
      </Link>
    </li>
  )
}

interface HomepageContentProps {
  /** FAQs to render visibly. Same source as JSON-LD FAQPage so the two stay in sync. */
  faqs: FaqItem[]
}

/**
 * Long-form SEO content rendered below the calculator. Solves the "homepage
 * is all UI, no text" problem identified in the SEO audit — without it, Google
 * has almost nothing to rank the homepage for against established Polish
 * banking/finance competitors.
 *
 * ~1700 Polish words across 5 sections:
 *   1. What the calculator does (entity recognition + feature surface)
 *   2. How mortgage payments are calculated (educational + keyword coverage)
 *   3. FAQs rendered visibly (paired with JSON-LD FAQPage on the same data)
 *   4. Differentiation vs bank calculators (E-E-A-T, brand identity)
 *   5. Internal links to articles (topical authority + link equity)
 *
 * Cites NBP, KNF, and Ministerstwo Finansów per AGENTS.md rule #6. No bank
 * names per rule #3 — comparison stays generic ("kalkulatory bankowe").
 */
export function HomepageContent({ faqs }: HomepageContentProps) {
  return (
    <section className="bg-white border-t border-border/60 mt-16">
      <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <article className="space-y-12 text-foreground leading-relaxed">
          <section>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Jak działa kalkulator kredytu hipotecznego RatyGo?
            </h1>
            <p className="text-[1.05rem]">
              <strong>RatyGo</strong> to bezpłatny kalkulator kredytu hipotecznego dla polskiego
              rynku. W kilka sekund pokazuje miesięczną ratę, całkowity koszt kredytu i sumę
              odsetek dla parametrów, które wpisujesz — ceny nieruchomości, wkładu własnego,
              okresu kredytowania oraz oprocentowania (WIBOR plus marża banku). Wszystkie
              obliczenia wykonują się lokalnie w przeglądarce, bez logowania i bez wysyłania
              danych na serwer.
            </p>
            <p className="mt-3 text-[1.05rem]">
              Kalkulator został zaprojektowany dla osób kupujących pierwsze mieszkanie lub dom,
              par planujących wspólny kredyt oraz obecnych kredytobiorców rozważających nadpłatę
              lub refinansowanie. Nie udzielamy kredytów, nie jesteśmy bankiem ani pośrednikiem
              kredytowym — to wyłącznie narzędzie obliczeniowe oparte na standardowych wzorach
              finansowych stosowanych w polskim sektorze bankowym.
            </p>
            <h3 className="text-lg font-bold text-foreground mt-6 mb-3">Co potrafi kalkulator?</h3>
            <ul className="list-disc pl-6 space-y-2 text-[1.02rem]">
              <li>
                <strong>Porównanie rat równych i malejących</strong> obok siebie — zobaczysz
                różnicę w miesięcznej kwocie i łącznym koszcie odsetek
              </li>
              <li>
                <strong>Symulacja nadpłat</strong> — sprawdź, ile zaoszczędzisz na odsetkach i o
                ile skrócisz okres spłaty nadpłacając dowolną kwotę miesięcznie
              </li>
              <li>
                <strong>Osobne suwaki WIBOR i marży banku</strong> — pozwalają precyzyjnie odwzorować
                ofertę dowolnego banku w Polsce
              </li>
              <li>
                <strong>Ocena zdolności kredytowej</strong> — orientacyjna analiza, na jaki kredyt
                Cię stać przy danym dochodzie netto, bazująca na zasadzie raty maksymalnie
                35% dochodu (Rekomendacja S Komisji Nadzoru Finansowego)
              </li>
              <li>
                <strong>Dodatkowe koszty zakupu</strong> — prowizja banku, ubezpieczenie
                nieruchomości, podatek PCC, taksa notarialna i wycena
              </li>
              <li>
                <strong>Porównanie kredytu z wynajmem</strong> — moduł pokazujący, po ilu latach
                zakup na kredyt staje się tańszy od najmu
              </li>
              <li>
                <strong>Zapisywanie do 3 scenariuszy</strong> obok siebie oraz udostępnianie wyniku
                przez link z zachowaniem wszystkich parametrów
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Jak liczy się rata kredytu hipotecznego?
            </h2>
            <p className="text-[1.05rem]">
              Rata kredytu hipotecznego składa się z dwóch części: <strong>kapitału</strong>
              {' '}(czyli kwoty pożyczonej od banku) i <strong>odsetek</strong> (kosztu pożyczenia
              tych pieniędzy). Sposób, w jaki te dwie części są rozłożone w czasie, zależy od
              wybranego systemu spłaty — rat równych lub rat malejących.
            </p>

            <h3 className="text-lg font-bold text-foreground mt-6 mb-3">
              Raty równe (annuitetowe)
            </h3>
            <p className="text-[1.05rem]">
              W systemie rat równych miesięczna kwota jest stała przez cały okres kredytowania.
              Na początku spłaty większa część raty pokrywa odsetki naliczane od wysokiego salda
              kapitału, a z czasem rośnie udział kapitału. Matematycznie: <em>rata = K × (r × (1+r)
              <sup>n</sup>) ÷ ((1+r)<sup>n</sup> − 1)</em>, gdzie <em>K</em> to kwota kredytu,
              {' '}<em>r</em> to miesięczna stopa procentowa (oprocentowanie roczne podzielone przez
              12), a <em>n</em> to liczba miesięcy spłaty.
            </p>

            <h3 className="text-lg font-bold text-foreground mt-6 mb-3">Raty malejące</h3>
            <p className="text-[1.05rem]">
              W systemie rat malejących stała jest tylko część kapitałowa raty — odsetki naliczane
              są od malejącego salda. Pierwsza rata jest najwyższa, a każda kolejna nieco niższa,
              bo z każdym miesiącem maleje kapitał, od którego liczone są odsetki. Suma zapłaconych
              odsetek przy ratach malejących jest niższa niż przy ratach równych, ale wymaga
              wyższej zdolności kredytowej w pierwszych latach. Szczegóły w artykule{' '}
              <BlogLink slug="raty-rowne-czy-malejace">raty równe czy malejące</BlogLink>.
            </p>

            <h3 className="text-lg font-bold text-foreground mt-6 mb-3">WIBOR plus marża banku</h3>
            <p className="text-[1.05rem]">
              Oprocentowanie kredytu hipotecznego w Polsce to suma dwóch składników:{' '}
              <strong>WIBOR</strong> (Warsaw Interbank Offered Rate) plus <strong>marża banku</strong>.
              WIBOR to stopa procentowa, po której banki pożyczają sobie pieniądze na rynku
              międzybankowym — zmienia się wraz z decyzjami Rady Polityki Pieniężnej Narodowego
              Banku Polskiego. Marża banku jest stała przez cały okres kredytu i to jedyny element
              oprocentowania, który możesz negocjować przed podpisaniem umowy. Więcej w artykule{' '}
              <BlogLink slug="co-to-jest-wibor">o WIBOR i oprocentowaniu kredytu</BlogLink>.
            </p>

            <h3 className="text-lg font-bold text-foreground mt-6 mb-3">Przykład: kredyt 400 000 zł</h3>
            <p className="text-[1.05rem]">
              Dla kredytu 400 000 zł na 25 lat przy oprocentowaniu 7,85% rata równa wynosi około
              {' '}<strong>3 048 zł</strong> i jest stała przez cały okres spłaty. W systemie rat
              malejących pierwsza rata to <strong>3 950 zł</strong>, a ostatnia{' '}
              <strong>1 342 zł</strong>. Łącznie przy ratach malejących zapłacisz o około 120 000 zł
              mniej odsetek niż przy ratach równych — ale wymagają one wyższego dochodu w pierwszych
              latach kredytu.
            </p>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Najczęściej zadawane pytania
            </h2>
            <p className="text-[1.05rem] mb-6">
              Najczęstsze pytania, które otrzymujemy o kalkulator i kredyt hipoteczny w Polsce.
            </p>
            <div className="space-y-5">
              {faqs.map((faq) => (
                <details
                  key={faq.question}
                  className="group rounded-xl border border-border/70 bg-white hover:border-indigo-200 transition-colors"
                >
                  <summary className="cursor-pointer list-none p-4 sm:p-5 font-semibold text-foreground flex items-start justify-between gap-3">
                    <span>{faq.question}</span>
                    <svg
                      className="w-5 h-5 mt-0.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </summary>
                  <p className="px-4 sm:px-5 pb-4 sm:pb-5 text-[1rem] text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </details>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Czym RatyGo różni się od kalkulatorów bankowych?
            </h2>
            <p className="text-[1.05rem]">
              Kalkulatory na stronach banków pokazują tylko własną ofertę i są zaprojektowane tak,
              żeby skierować Cię do podpisania umowy. RatyGo nie sprzedaje produktów finansowych
              — pokazuje obiektywne liczby niezależnie od konkretnej oferty kredytowej.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4 text-[1.02rem]">
              <li>
                <strong>Porównanie obu systemów spłaty.</strong> Większość kalkulatorów bankowych
                pokazuje tylko raty równe. RatyGo pokazuje raty równe i malejące obok siebie z
                pełną sumą odsetek — często to różnica kilkudziesięciu tysięcy złotych w skali
                całej spłaty.
              </li>
              <li>
                <strong>Symulacja nadpłat.</strong> Kalkulatory bankowe rzadko umożliwiają
                planowanie nadpłat. RatyGo pokazuje, ile zaoszczędzisz na odsetkach i o ile
                skrócisz okres kredytu nadpłacając dowolną kwotę miesięcznie. Szczegóły w
                artykule o{' '}
                <BlogLink slug="nadplata-kredytu-hipotecznego">nadpłacie kredytu</BlogLink>.
              </li>
              <li>
                <strong>Kredyt czy wynajem.</strong> Bankowe kalkulatory nie pokazują, kiedy
                kupno opłaca się bardziej niż wynajem. RatyGo ma osobny moduł porównawczy —{' '}
                <BlogLink slug="kredyt-czy-wynajem">analiza kredyt vs wynajem</BlogLink>.
              </li>
              <li>
                <strong>Bez zbierania danych.</strong> Wszystkie obliczenia w RatyGo dzieją się
                lokalnie w Twojej przeglądarce. Nie pytamy o adres email, nie wysyłamy parametrów
                kredytu na żaden serwer, nie sprzedajemy danych pośrednikom.
              </li>
              <li>
                <strong>Bez rekomendacji banków.</strong> Nie wskazujemy „najlepszego banku" — bo
                najlepsza oferta zależy od Twojej sytuacji, a my nie mamy dostępu do polityk
                kredytowych poszczególnych instytucji. Pokazujemy liczby, decyzję podejmujesz
                samodzielnie.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Poradniki o kredytach hipotecznych w Polsce
            </h2>
            <p className="text-[1.05rem]">
              Zanim podpiszesz umowę, warto rozumieć co najmniej kilka kluczowych pojęć i pułapek.
              Poniżej wybrane artykuły napisane z myślą o osobach kupujących pierwsze mieszkanie
              oraz obecnych kredytobiorcach, którzy planują nadpłatę albo refinansowanie.
            </p>
            <ul className="mt-5 grid gap-3 sm:grid-cols-2 text-[1rem]">
              <PoradnikCard
                slug="jak-dostac-kredyt-hipoteczny-na-pierwsze-mieszkanie"
                title="Jak dostać kredyt hipoteczny na pierwsze mieszkanie?"
                description="Cały proces krok po kroku — od zdolności kredytowej po akt notarialny."
              />
              <PoradnikCard
                slug="jak-obliczyc-zdolnosc-kredytowa"
                title="Jak obliczyć zdolność kredytową?"
                description="Czynniki branych pod uwagę przez banki, formuła DTI, sposoby zwiększenia zdolności."
              />
              <PoradnikCard
                slug="wklad-wlasny-kredyt-hipoteczny"
                title="Wkład własny — ile potrzebujesz?"
                description="Minimalny wkład wg Rekomendacji S KNF, źródła wkładu, ubezpieczenie niskiego wkładu."
              />
              <PoradnikCard
                slug="rrso-kredyt-hipoteczny"
                title="Co to jest RRSO i jak czytać oferty banków?"
                description="Rzeczywista Roczna Stopa Oprocentowania — jedyna liczba pokazująca prawdziwy koszt."
              />
              <PoradnikCard
                slug="najczestsze-bledy-kredyt-hipoteczny"
                title="7 najczęstszych błędów przy kredycie hipotecznym"
                description="Pułapki kosztujące ponad 200 000 zł w skali całej spłaty — jak ich uniknąć."
              />
              <PoradnikCard
                slug="stale-czy-zmienne-oprocentowanie"
                title="Stałe czy zmienne oprocentowanie?"
                description="Kiedy warto wybrać każdy z wariantów i ile dopłaca się za przewidywalność raty."
              />
            </ul>
          </section>

          <p className="text-xs text-muted-foreground pt-6 border-t border-border/50">
            <span className="font-semibold">Źródła danych i regulacji:</span>{' '}
            Narodowy Bank Polski (stopy procentowe, dane WIBOR) · Komisja Nadzoru Finansowego
            (Rekomendacja S — minimalny wkład własny, maksymalny okres kredytowania, próg
            rata/dochód) · Ministerstwo Finansów (ustawa o kredycie hipotecznym z 23 marca
            2017 roku — definicja RRSO, obowiązki informacyjne banków, limit opłat za nadpłatę).
            RatyGo to narzędzie obliczeniowe i edukacyjne — nie udziela kredytów ani nie jest
            pośrednikiem kredytowym.
          </p>
        </article>
      </div>
    </section>
  )
}
